import { useEffect, useState, useCallback } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Download, Loader2, TrendingUp } from "lucide-react";
import { opApi } from "@/lib/api";
import { toast } from "sonner";

const money = (n) => "$" + Number(n || 0).toFixed(2);
const ymd = (d) => d.toISOString().slice(0, 10);

function rangeFor(preset) {
  const now = new Date();
  const t = ymd(now);
  const minus = (n) => { const d = new Date(now); d.setDate(d.getDate() - n); return ymd(d); };
  switch (preset) {
    case "today": return { from: t, to: t };
    case "yesterday": { const y = minus(1); return { from: y, to: y }; }
    case "7d": return { from: minus(6), to: t };
    case "30d": return { from: minus(29), to: t };
    case "mtd": { const d = new Date(now.getFullYear(), now.getMonth(), 1); return { from: ymd(d), to: t }; }
    default: return { from: t, to: t };
  }
}

const PRESETS = [["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 days"], ["30d", "Last 30 days"], ["mtd", "This month"]];

export function ReportsTab() {
  const [preset, setPreset] = useState("7d");
  const [range, setRange] = useState(rangeFor("7d"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const { data } = await opApi.get("/tenant/reports", { params: { date_from: r.from, date_to: r.to } });
      setData(data);
    } catch (e) { setData(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(range); }, [range, load]);

  const pick = (p) => { setPreset(p); setRange(rangeFor(p)); };
  const setCustom = (key, val) => { setPreset("custom"); setRange((r) => ({ ...r, [key]: val })); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await opApi.get("/tenant/reports/export", { params: { date_from: range.from, date_to: range.to }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `sales_${range.from}_${range.to}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Couldn't export the report. Please try again."); } finally { setExporting(false); }
  };

  const s = data?.summary || {};
  const ts = data?.timeseries || [];
  const items = data?.top_items || [];
  const cats = data?.categories || [];
  const channels = data?.channels || [];
  const locations = data?.locations || [];
  const maxCat = Math.max(1, ...cats.map((c) => c.revenue));
  const maxCh = Math.max(1, ...channels.map((c) => c.revenue));
  const maxLoc = Math.max(1, ...locations.map((l) => l.revenue));

  return (
    <div className="space-y-5" data-testid="reports-tab">
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map(([k, label]) => (
          <button key={k} onClick={() => pick(k)} data-testid={`report-range-${k}`}
            className={"h-8 px-3 text-xs border rounded-none transition-colors " + (preset === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary")}>
            {label}
          </button>
        ))}
        <div className="flex items-center gap-1 ml-auto">
          <input type="date" value={range.from} onChange={(e) => setCustom("from", e.target.value)} className="h-8 rounded-none border border-input bg-background px-2 text-xs" data-testid="report-from" />
          <span className="text-muted-foreground text-xs">to</span>
          <input type="date" value={range.to} onChange={(e) => setCustom("to", e.target.value)} className="h-8 rounded-none border border-input bg-background px-2 text-xs" data-testid="report-to" />
          <Button variant="outline" className="rounded-none h-8 gap-1 text-xs ml-1" onClick={exportCsv} disabled={exporting} data-testid="report-export">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export CSV
          </Button>
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground py-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading report…</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Sales" value={money(s.total)} testid="kpi-sales" />
            <Kpi label="Orders" value={s.orders || 0} testid="kpi-orders" />
            <Kpi label="Avg ticket" value={money(s.avg_ticket)} testid="kpi-avg" />
            <Kpi label="Collected" value={money(s.collected)} testid="kpi-collected" />
            <Kpi label="Outstanding" value={money(s.outstanding)} testid="kpi-outstanding" />
          </div>
          {data.jobs && (data.cart_flows || []).includes("quote") && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-jobs">
              <Kpi label="Job revenue" value={money(data.jobs.revenue)} testid="kpi-job-revenue" />
              <Kpi label="Jobs completed" value={data.jobs.completed} testid="kpi-job-completed" />
              <Kpi label="Visits scheduled" value={data.jobs.scheduled} testid="kpi-job-scheduled" />
              <Kpi label="Open requests" value={data.jobs.open_requests} testid="kpi-job-open" />
            </div>
          )}
          {data.appointments && ((data.cart_flows || []).includes("appointment") || data.appointments.total > 0) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-appointments">
              <Kpi label="Appointment revenue" value={money(data.appointments.revenue)} testid="kpi-appt-revenue" />
              <Kpi label="Completed" value={data.appointments.completed} testid="kpi-appt-completed" />
              <Kpi label="Booked (all)" value={data.appointments.total} testid="kpi-appt-total" />
              <Kpi label="Cancelled / no-show" value={data.appointments.cancelled + data.appointments.no_show} testid="kpi-appt-cancelled" />
            </div>
          )}

          <div className="border border-border p-4">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3"><TrendingUp className="h-4 w-4" /> Daily sales</div>
            {ts.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={ts} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => "$" + v} width={48} />
                  <Tooltip formatter={(v, n) => (n === "sales" ? money(v) : v)} labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, borderRadius: 0 }} />
                  <Bar dataKey="sales" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty>No sales in this range.</Empty>}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Panel title={`Top items`}>
              {items.length ? items.map((it, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-t border-border py-1.5" data-testid={`report-item-${i}`}>
                  <span className="truncate mr-2">{it.name}</span>
                  <span className="text-muted-foreground shrink-0"><span className="font-mono">{it.qty}</span> · {money(it.revenue)}</span>
                </div>
              )) : <Empty>No items sold.</Empty>}
            </Panel>

            <Panel title="Category mix">
              {cats.length ? cats.map((c, i) => (
                <div key={i} className="py-1.5 border-t border-border" data-testid={`report-cat-${i}`}>
                  <div className="flex items-center justify-between text-sm"><span className="truncate mr-2">{c.name}</span><span className="font-mono text-muted-foreground">{money(c.revenue)}</span></div>
                  <div className="h-1.5 bg-secondary mt-1"><div className="h-full bg-primary" style={{ width: `${(c.revenue / maxCat) * 100}%` }} /></div>
                </div>
              )) : <Empty>No category data.</Empty>}
            </Panel>

            <Panel title="Channel mix">
              {channels.length ? channels.map((c, i) => (
                <div key={i} className="py-1.5 border-t border-border" data-testid={`report-channel-${i}`}>
                  <div className="flex items-center justify-between text-sm"><span className="capitalize">{c.source}</span><span className="font-mono text-muted-foreground">{money(c.revenue)} · {c.orders}</span></div>
                  <div className="h-1.5 bg-secondary mt-1"><div className="h-full bg-primary" style={{ width: `${(c.revenue / maxCh) * 100}%` }} /></div>
                </div>
              )) : <Empty>No channel data.</Empty>}
            </Panel>
          </div>

          {locations.length > 1 && (
            <div className="border border-border p-4" data-testid="report-locations">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">By location <span className="normal-case tracking-normal">· which shops &amp; stops perform best</span></div>
              {locations.map((l, i) => (
                <div key={l.location_id} className="py-2 border-t border-border" data-testid={`report-location-${i}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="truncate mr-2 font-medium">{l.name}</span>
                    <span className="font-mono text-muted-foreground shrink-0">{money(l.revenue)} · {l.orders} order{l.orders === 1 ? "" : "s"} · {money(l.avg_ticket)} avg</span>
                  </div>
                  <div className="h-1.5 bg-secondary mt-1.5"><div className="h-full bg-primary" style={{ width: `${(l.revenue / maxLoc) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono font-bold text-lg">{value}</div>
    </div>
  );
}
function Panel({ title, children }) {
  return (
    <div className="border border-border p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">{title}</div>
      {children}
    </div>
  );
}
function Empty({ children }) { return <div className="text-xs text-muted-foreground py-3">{children}</div>; }
